import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";

export type Role = "superadmin" | "admin" | null;

type AuthCtx = {
  user: User | null;
  role: Role;
  isAdmin: boolean;
  /** Yetki verip alabilen tek rol. */
  isSuperAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

/**
 * Proje sahibi. Aynı UID ve e-posta firestore.rules içinde de sabit — asıl
 * güvenlik orada zorlanıyor, buradaki kontrol sadece arayüzü açmak için.
 *
 * Custom claim yolu bu projede kullanılamıyor: callable fonksiyonlar
 * kuruluş politikası yüzünden IAM'de 403 alıyor, tetikleyici üzerinden
 * denendiğinde de servis hesabı setCustomUserClaims çağıramıyor. Bu yüzden
 * yönetici yetkisi `admins/{uid}` belgesiyle taşınıyor.
 */
export const SUPER_ADMIN_UID = "l7UQn5VKZudTk1ZUEVyHT1ZoT7C2";
export const SUPER_ADMIN_EMAIL = "serdarbey62@gmail.com";

export function isSuperAdminUser(u: User | null): boolean {
  if (!u) return false;
  return (
    u.uid === SUPER_ADMIN_UID ||
    (u.email ?? "").toLowerCase() === SUPER_ADMIN_EMAIL
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubRole: (() => void) | null = null;

    const unsub = onAuthStateChanged(auth, async (u) => {
      unsubRole?.();
      unsubRole = null;
      setUser(u);

      if (!u) {
        setRole(null);
        setLoading(false);
        return;
      }

      if (isSuperAdminUser(u)) {
        setRole("superadmin");
        setLoading(false);
        return;
      }

      // Eski claim yolu hâlâ okunuyor: bir gün claim'ler açılırsa panel
      // kendiliğinden çalışsın diye. Bugün pratikte hep false.
      const token = await u.getIdTokenResult(true);
      const claimAdmin = token.claims.admin === true;

      // Rol belgesini canlı dinliyoruz: süper yönetici yetkiyi verdiği anda
      // karşı taraf sayfayı yenilemeden panele girebiliyor, yetki alındığında
      // da aynı anda düşüyor.
      unsubRole = onSnapshot(
        doc(db, "admins", u.uid),
        (snap) => {
          setRole(snap.exists() || claimAdmin ? "admin" : null);
          setLoading(false);
        },
        () => {
          setRole(claimAdmin ? "admin" : null);
          setLoading(false);
        }
      );
    });

    return () => {
      unsubRole?.();
      unsub();
    };
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      user,
      role,
      isAdmin: role !== null,
      isSuperAdmin: role === "superadmin",
      loading,
      signIn: async (email, password) => {
        await signInWithEmailAndPassword(auth, email, password);
      },
      signOut: async () => {
        await signOut(auth);
      },
    }),
    [user, role, loading]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
